import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  approveEmail,
  bulkApproveEmails,
  bulkDiscardEmails,
  discardEmail,
  listEmailLog,
  type EmailKind,
  type EmailLogRow,
  type EmailStatus,
} from '../lib/api';
import { WafDataGrid } from '../components/WafDataGrid';

const KIND_OPTIONS: EmailKind[] = [
  'invite',
  'password_reset',
  'password_changed',
  'resend',
  'onboarding',
];
const STATUS_OPTIONS: EmailStatus[] = [
  'queued',
  'sending',
  'sent',
  'failed',
  'staged',
  'blocked',
  'discarded',
];

const STATUS_COLORS: Record<EmailStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  queued: 'warning',
  sending: 'info',
  sent: 'success',
  failed: 'error',
  staged: 'info',
  blocked: 'warning',
  discarded: 'default',
};

function prettyKind(k: EmailKind): string {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Normalise the DataGrid selection model (array in v7, { ids } in v8) to ids.
function selectionToIds(model: GridRowSelectionModel): number[] {
  const raw = Array.isArray(model)
    ? model
    : Array.from((model as unknown as { ids?: Iterable<unknown> }).ids ?? []);
  return raw.map((v) => Number(v));
}

export function EmailLog() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<EmailLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [recipient, setRecipient] = useState('');
  const [kind, setKind] = useState<EmailKind | ''>('');
  const [status, setStatus] = useState<EmailStatus | ''>('');

  const [selection, setSelection] = useState<GridRowSelectionModel>([] as unknown as GridRowSelectionModel);
  const [preview, setPreview] = useState<EmailLogRow | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return listEmailLog({
      recipient: recipient.trim() || undefined,
      kind: kind || undefined,
      status: status || undefined,
      limit: 2000,
    })
      .then((res) => {
        setRows(res.rows);
        setTotal(res.total);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'failed to load'))
      .finally(() => setLoading(false));
  }, [recipient, kind, status]);

  useEffect(() => {
    let cancelled = false;
    // Debounce the recipient text input so we don't fire one query per keystroke.
    const t = setTimeout(() => {
      if (!cancelled) load();
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [load]);

  // Ids of selected rows that are actually staged — the only ones we can act on.
  const stagedSelectedIds = useMemo(() => {
    const ids = new Set(selectionToIds(selection));
    return rows.filter((r) => ids.has(r.id) && r.status === 'staged').map((r) => r.id);
  }, [selection, rows]);

  async function runAction(fn: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await fn());
      setSelection([] as unknown as GridRowSelectionModel);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'action failed');
    } finally {
      setBusy(false);
    }
  }

  const sendSelected = () =>
    runAction(async () => {
      // Chunk so we never hold one giant request open while SES sends hundreds
      // of emails; the backend also paces the actual handoffs. If a batch is
      // interrupted, the Email Log is the source of truth for per-message status
      // and re-selecting the still-staged rows resumes safely.
      const ids = stagedSelectedIds;
      const BATCH = 25;
      let sent = 0;
      for (let i = 0; i < ids.length; i += BATCH) {
        const res = await bulkApproveEmails(ids.slice(i, i + BATCH));
        sent += res.sent;
      }
      return `Sent ${sent} of ${ids.length} staged email(s).`;
    });

  const discardSelected = () =>
    runAction(async () => {
      // Chunk like sendSelected so discarding a large failed batch doesn't hold
      // one giant request open and time out.
      const ids = stagedSelectedIds;
      const BATCH = 25;
      let discarded = 0;
      for (let i = 0; i < ids.length; i += BATCH) {
        const res = await bulkDiscardEmails(ids.slice(i, i + BATCH));
        discarded += res.discarded;
      }
      return `Discarded ${discarded} of ${ids.length} staged email(s).`;
    });

  const sendOne = (id: number) =>
    runAction(async () => {
      const res = await approveEmail(id);
      if (!res.ok) throw new Error(res.error ?? 'Could not send');
      setPreview(null);
      return 'Email sent.';
    });

  const discardOne = (id: number) =>
    runAction(async () => {
      const res = await discardEmail(id);
      if (!res.ok) throw new Error(res.error ?? 'Could not discard');
      setPreview(null);
      return 'Email discarded.';
    });

  const columns = useMemo<GridColDef<EmailLogRow>[]>(
    () => [
      {
        field: 'created_at',
        headerName: 'When',
        width: 170,
        valueFormatter: (v: string) => (v ? new Date(v).toLocaleString() : ''),
      },
      { field: 'recipient_email', headerName: 'Recipient', flex: 1, minWidth: 220 },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 150,
        valueFormatter: (v: EmailKind) => prettyKind(v),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (p: GridRenderCellParams<EmailLogRow, EmailStatus>) => (
          <Chip size="small" color={STATUS_COLORS[p.value as EmailStatus]} label={p.value} />
        ),
      },
      { field: 'subject', headerName: 'Subject', flex: 1.2, minWidth: 240 },
      {
        field: 'error_message',
        headerName: 'Detail',
        flex: 1,
        minWidth: 200,
        renderCell: (p: GridRenderCellParams<EmailLogRow, string | null>) =>
          p.value ? (
            <Typography variant="caption" color="text.secondary" title={p.value}>
              {p.value}
            </Typography>
          ) : (
            ''
          ),
      },
      {
        field: 'sent_at',
        headerName: 'Sent at',
        width: 170,
        valueFormatter: (v: string | null) => (v ? new Date(v).toLocaleString() : ''),
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h4" fontWeight={600}>
        Email log
      </Typography>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="Recipient contains"
            size="small"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
          />
          <TextField
            label="Kind"
            size="small"
            select
            value={kind}
            onChange={(e) => setKind((e.target.value as EmailKind | '') ?? '')}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          >
            <MenuItem value="">All</MenuItem>
            {KIND_OPTIONS.map((k) => (
              <MenuItem key={k} value={k}>
                {prettyKind(k)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Status"
            size="small"
            select
            value={status}
            onChange={(e) => setStatus((e.target.value as EmailStatus | '') ?? '')}
            sx={{ minWidth: { xs: '100%', sm: 160 } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="staged">Staged (awaiting approval)</MenuItem>
            <Divider />
            {STATUS_OPTIONS.filter((s) => s !== 'staged').map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* Approval action bar — appears when staged rows are selected. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            {stagedSelectedIds.length > 0
              ? `${stagedSelectedIds.length} staged email(s) selected`
              : 'Select staged emails to send or discard.'}
          </Typography>
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          <Button
            variant="contained"
            color="success"
            size="small"
            disabled={busy || stagedSelectedIds.length === 0}
            onClick={sendSelected}
          >
            Send selected
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={busy || stagedSelectedIds.length === 0}
            onClick={discardSelected}
          >
            Discard selected
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ height: 'calc(100vh - 340px)', minHeight: 420 }}>
        <WafDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection
          rowSelectionModel={selection}
          onRowSelectionModelChange={(m) => setSelection(m)}
          onRowClick={(p) => setPreview(p.row as EmailLogRow)}
          initialState={{
            sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
          }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        Showing {rows.length} of {total} matching rows. Click a row to preview the email.
      </Typography>

      <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth="md" fullWidth fullScreen={fullScreen}>
        {preview && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color={STATUS_COLORS[preview.status]} label={preview.status} />
                <span>{preview.subject}</span>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                To: {preview.recipient_email} · {prettyKind(preview.kind)} ·{' '}
                {new Date(preview.created_at).toLocaleString()}
              </Typography>
              {preview.error_message && (
                <Alert severity="info" sx={{ my: 1 }}>
                  {preview.error_message}
                </Alert>
              )}
              <Divider sx={{ my: 1 }} />
              {preview.body_html ? (
                <Box
                  sx={{ '& a': { color: 'primary.main' } }}
                  // Admin-only preview of our own templated content.
                  dangerouslySetInnerHTML={{ __html: preview.body_html }}
                />
              ) : (
                <Typography variant="body2" whiteSpace="pre-wrap">
                  {preview.body_text ?? '(no body stored)'}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              {preview.status === 'staged' && (
                <>
                  <Button color="error" disabled={busy} onClick={() => discardOne(preview.id)}>
                    Discard
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={busy}
                    onClick={() => sendOne(preview.id)}
                  >
                    Send now
                  </Button>
                </>
              )}
              <Button onClick={() => setPreview(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}
